"use client";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

import { Header as HeaderType } from "@/types/blocks/header";
import Icon from "@/components/icon";
import { Link, useRouter } from "@/i18n/navigation";
import { Menu } from "lucide-react";
import SignToggle from "@/components/sign/toggle";
import { useEffect } from "react";
import { useAppContext } from "@/contexts/app";

import { cn } from "@/lib/utils";
import { trackEvent } from "@/lib/analytics";
import NextImage from "next/image";
import { BRAND_NAME, SUPPORT_EMAIL, VISIBLE_LOGO_SRC } from "@/lib/brand";
import { normalizeLandingUrl, normalizeNavItems } from "@/lib/landing-url";

const getPrefetchDelay = () => {
  if (typeof navigator === "undefined") {
    return 8000;
  }

  const connection = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
  if (!connection) {
    return 8000;
  }

  if (connection.saveData) {
    return 20000;
  }

  const effectiveType = connection.effectiveType || "";
  if (effectiveType === "slow-2g" || effectiveType === "2g") {
    return 20000;
  }

  if (effectiveType === "3g") {
    return 12000;
  }

  return 8000;
};

const isMarketplaceUrl = (url?: string) => {
  if (!url) {
    return false;
  }

  return (
    url.includes("/pricing") ||
    url.includes("#pricing") ||
    url.includes("/listings") ||
    url.includes("/marketplace")
  );
};

const resolveMarketplaceSource = () => {
  if (typeof window === "undefined") {
    return "header";
  }

  const hash = window.location.hash?.toLowerCase();
  if (hash === "#faq") {
    return "faq";
  }
  if (hash === "#feature" || hash === "#features") {
    return "feature";
  }

  return "header";
};

const trackMarketplaceView = (url?: string, sourceOverride?: string) => {
  if (!isMarketplaceUrl(url)) {
    return;
  }

  // Keep the legacy event name to avoid breaking existing analytics dashboards.
  trackEvent("pricing_viewed", {
    ui_source: sourceOverride || resolveMarketplaceSource(),
  });
};

export default function Header({ header }: { header: HeaderType }) {
  if (header.disabled) {
    return null;
  }

  const isStylesMenu = (name?: string) => name === "styles";

  const router = useRouter();
  const { user } = useAppContext();
  const defaultNavItems = normalizeNavItems(header.nav?.items);
  const navItems = user
    ? normalizeNavItems([
        { title: "Browse", url: "/marketplace" },
        { title: "Pricing", url: "/pricing" },
        { title: "Features", url: "/#features" },
        { title: "Blog", url: "/posts" },
      ])
    : defaultNavItems;
  const shouldShowSign = header.show_sign !== false;
  const headerButtons = (header.buttons ?? []).filter((item) => {
    const url = item.url?.trim().toLowerCase() ?? "";

    // Keep CTA buttons, but hide support email actions.
    return !(
      url.startsWith("mailto:support@") ||
      url.includes(SUPPORT_EMAIL)
    );
  });
  const visibleHeaderButtons = user
    ? []
    : headerButtons.map((item) => ({
        ...item,
        url: normalizeLandingUrl(item.url),
      }));
  const stylesIndex = navItems.findIndex((item) => item.name === "styles");
  const marketplaceIndex = navItems.findIndex(
    (item) =>
      item.url?.includes("/listings") ||
      item.url?.includes("/marketplace") ||
      item.url?.includes("/pricing")
  );
  if (
    stylesIndex > -1 &&
    marketplaceIndex > -1 &&
    stylesIndex > marketplaceIndex
  ) {
    const [stylesItem] = navItems.splice(stylesIndex, 1);
    navItems.splice(marketplaceIndex, 0, stylesItem);
  }

  useEffect(() => {
    let idleId: number | null = null;
    let timeoutId: ReturnType<typeof globalThis.setTimeout> | null = null;

    const schedulePrefetch = () => {
      const delay = getPrefetchDelay();
      const runPrefetch = () => router.prefetch("/posts");

      if ("requestIdleCallback" in window) {
        idleId = window.requestIdleCallback(runPrefetch, { timeout: delay });
      } else {
        timeoutId = globalThis.setTimeout(runPrefetch, delay);
      }
    };

    if (document.readyState === "complete") {
      schedulePrefetch();
    } else {
      window.addEventListener("load", schedulePrefetch, { once: true });
    }

    return () => {
      if (idleId !== null) window.cancelIdleCallback(idleId);
      if (timeoutId !== null) globalThis.clearTimeout(timeoutId);
      window.removeEventListener("load", schedulePrefetch);
    };
  }, [router]);

  const brandAlt =
    header.brand?.logo?.alt || header.brand?.title || `${BRAND_NAME} logo`;

  return (
    <section className="landing-header py-3 md:py-4">
      <div className="container relative z-10">
        <nav className="hidden items-center justify-between lg:flex">
          <div className="flex items-center gap-8">
            <Link
              href={(header.brand?.url as any) || "/"}
              className="flex items-center gap-2 text-foreground"
            >
              {header.brand && (
                <NextImage
                  src={VISIBLE_LOGO_SRC}
                  alt={brandAlt}
                  width={36}
                  height={36}
                  sizes="36px"
                  data-brand-logo="header"
                  className="h-auto w-9 rounded-lg"
                />
              )}
              {header.brand?.title && (
                <span className="text-xl font-semibold tracking-tight text-foreground">
                  {header.brand?.title || ""}
                </span>
              )}
            </Link>
            <div className="flex items-center">
              <NavigationMenu>
                <NavigationMenuList>
                  {navItems.map((item, i) => {
                    if (item.children && item.children.length > 0) {
                      return (
                        <NavigationMenuItem
                          key={i}
                          className="text-muted-foreground"
                        >
                          <NavigationMenuTrigger className="landing-nav-trigger rounded-lg px-3">
                            {item.icon && (
                              <Icon
                                name={item.icon}
                                className="size-4 shrink-0 mr-2"
                              />
                            )}
                            <span>{item.title}</span>
                          </NavigationMenuTrigger>
                          <NavigationMenuContent>
                            {isStylesMenu(item.name) ? (
                              <div className="w-[560px] rounded-[24px] border border-border/50 bg-background/95 p-4 shadow-[0_24px_56px_rgba(0,0,0,0.35)] backdrop-blur-xl">
                                <ul className="grid gap-3 md:grid-cols-2">
                                  {item.children.map((iitem, ii) => (
                                    <li key={ii}>
                                      <NavigationMenuLink asChild>
                                        <Link
                                          className="group flex items-center gap-3 rounded-2xl border border-border/40 bg-card/50 p-3 leading-none transition hover:border-primary/30 hover:bg-primary/10"
                                          href={iitem.url as any}
                                          target={iitem.target}
                                          onClick={() => trackMarketplaceView(iitem.url)}
                                        >
                                          <div className="relative size-12 overflow-hidden rounded-xl border border-border/50 bg-background/80">
                                            {iitem.image?.src ? (
                                              <NextImage
                                                src={iitem.image.src}
                                                alt={iitem.image.alt || iitem.title || "Style preview"}
                                                fill
                                                sizes="48px"
                                                className="object-cover"
                                              />
                                            ) : null}
                                          </div>
                                          <div>
                                            <div className="text-sm font-semibold text-foreground">
                                              {iitem.title}
                                            </div>
                                          </div>
                                        </Link>
                                      </NavigationMenuLink>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ) : (
                              <ul className="w-80 p-3">
                                <NavigationMenuLink>
                                  {item.children.map((iitem, ii) => (
                                    <li key={ii}>
                                      <Link
                                        className={cn(
                                          "flex select-none gap-4 rounded-xl p-3 leading-none no-underline outline-hidden transition-colors hover:bg-muted hover:text-foreground focus:bg-muted focus:text-foreground"
                                        )}
                                        href={iitem.url as any}
                                        target={iitem.target}
                                        onClick={() => trackMarketplaceView(iitem.url)}
                                      >
                                        {iitem.icon && (
                                          <Icon
                                            name={iitem.icon}
                                            className="size-5 shrink-0"
                                          />
                                        )}
                                        <div>
                                          <div className="text-sm font-semibold">
                                            {iitem.title}
                                          </div>
                                          <p className="text-sm leading-snug text-muted-foreground">
                                            {iitem.description}
                                          </p>
                                        </div>
                                      </Link>
                                    </li>
                                  ))}
                                </NavigationMenuLink>
                              </ul>
                            )}
                          </NavigationMenuContent>
                        </NavigationMenuItem>
                      );
                    }

                    return (
                      <NavigationMenuItem key={i}>
                        <Link
                          className="landing-nav-link inline-flex h-10 items-center justify-center rounded-lg px-3 text-sm font-medium"
                          href={item.url as any}
                          target={item.target}
                          onClick={() => trackMarketplaceView(item.url)}
                        >
                          {item.icon && (
                            <Icon
                              name={item.icon}
                              className="size-4 shrink-0 mr-0"
                            />
                          )}
                          {item.title}
                        </Link>
                      </NavigationMenuItem>
                    );
                  })}
                </NavigationMenuList>
              </NavigationMenu>
            </div>
          </div>
          <div className="shrink-0 flex items-center gap-2">
            {shouldShowSign && <SignToggle />}
            {visibleHeaderButtons.map((item, i) => {
              return (
                <Button
                  key={i}
                  variant={item.variant}
                  className="landing-hero-button bg-primary"
                >
                  <Link
                    href={item.url as any}
                    target={item.target || ""}
                    className="flex items-center gap-1 cursor-pointer"
                    onClick={() => trackMarketplaceView(item.url)}
                  >
                    {item.title}
                    {item.icon && (
                      <Icon name={item.icon} className="size-4 shrink-0" />
                    )}
                  </Link>
                </Button>
              );
            })}
          </div>
        </nav>

        <div className="block lg:hidden">
          <div className="flex items-center justify-between gap-2">
            <Link
              href={(header.brand?.url || "/") as any}
              className="flex items-center gap-2 text-foreground"
            >
              {header.brand && (
                <NextImage
                  src={VISIBLE_LOGO_SRC}
                  alt={brandAlt}
                  width={28}
                  height={28}
                  sizes="28px"
                  data-brand-logo="header-mobile"
                  className="h-auto w-7"
                />
              )}
              {header.brand?.title && (
                <span className="text-xl font-bold tracking-tight">
                  {header.brand?.title || ""}
                </span>
              )}
            </Link>
            <div className="flex items-center gap-2">
              <Sheet>
                <SheetTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label="Open menu"
                    className="landing-hero-button border border-border/50 bg-card/70 text-foreground hover:bg-muted"
                  >
                    <Menu className="size-4" />
                  </Button>
                </SheetTrigger>
                <SheetContent className="overflow-y-auto border-border/50 bg-background/95 text-foreground shadow-[0_24px_64px_rgba(0,0,0,0.35)] backdrop-blur-xl">
                  <SheetHeader>
                    <SheetTitle>
                      <Link
                        href={(header.brand?.url || "/") as any}
                        className="flex items-center gap-2"
                      >
                        {header.brand && (
                          <NextImage
                            src={VISIBLE_LOGO_SRC}
                            alt={brandAlt}
                            width={28}
                            height={28}
                            sizes="28px"
                            data-brand-logo="header-sheet"
                            className="h-auto w-7 rounded-lg"
                          />
                        )}
                        {header.brand?.title && (
                          <span className="text-xl font-semibold">
                            {header.brand?.title || ""}
                          </span>
                        )}
                      </Link>
                    </SheetTitle>
                  </SheetHeader>
                  <div className="my-4 flex flex-col gap-4">
                    <Accordion type="single" collapsible className="w-full">
                      {navItems.map((item, i) => {
                        if (item.children && item.children.length > 0) {
                          return (
                            <AccordionItem
                              key={i}
                              value={item.title || ""}
                              className="border-b-0"
                            >
                              <AccordionTrigger className="mb-4 py-0 px-4 font-semibold hover:no-underline text-left">
                                {item.title}
                              </AccordionTrigger>
                              <AccordionContent className="mt-2">
                                {isStylesMenu(item.name) ? (
                                  <div className="grid gap-3">
                                    {item.children.map((iitem, ii) => (
                                      <Link
                                        key={ii}
                                        className="flex items-center gap-3 rounded-xl border border-border/50 bg-card/60 p-3 transition-colors hover:bg-muted"
                                        href={iitem.url as any}
                                        target={iitem.target}
                                        onClick={() => trackMarketplaceView(iitem.url)}
                                      >
                                        <div className="relative size-10 overflow-hidden rounded-lg border border-border/50 bg-background/80">
                                          {iitem.image?.src ? (
                                            <NextImage
                                              src={iitem.image.src}
                                              alt={iitem.image.alt || iitem.title || "Style preview"}
                                              fill
                                              sizes="40px"
                                              className="object-cover"
                                            />
                                          ) : null}
                                        </div>
                                        <div>
                                          <div className="text-sm font-semibold">
                                            {iitem.title}
                                          </div>
                                        </div>
                                      </Link>
                                    ))}
                                  </div>
                                ) : (
                                  item.children.map((iitem, ii) => (
                                    <Link
                                      key={ii}
                                      className={cn(
                                        "flex select-none gap-4 rounded-xl p-3 leading-none outline-hidden transition-colors hover:bg-muted hover:text-foreground focus:bg-muted focus:text-foreground"
                                      )}
                                      href={iitem.url as any}
                                      target={iitem.target}
                                      onClick={() => trackMarketplaceView(iitem.url)}
                                    >
                                      {iitem.icon && (
                                        <Icon
                                          name={iitem.icon}
                                          className="size-4 shrink-0"
                                        />
                                      )}
                                      <div>
                                        <div className="text-sm font-semibold">
                                          {iitem.title}
                                        </div>
                                        <p className="text-sm leading-snug text-muted-foreground">
                                          {iitem.description}
                                        </p>
                                      </div>
                                    </Link>
                                  ))
                                )}
                              </AccordionContent>
                            </AccordionItem>
                          );
                        }
                        return (
                          <Link
                            key={i}
                            href={item.url as any}
                            target={item.target}
                            className="my-4 flex items-center gap-2 px-4 font-semibold text-foreground/90"
                            onClick={() => trackMarketplaceView(item.url)}
                          >
                            {item.icon && (
                              <Icon
                                name={item.icon}
                                className="size-4 shrink-0"
                              />
                            )}
                            {item.title}
                          </Link>
                        );
                      })}
                    </Accordion>
                  </div>
                  <div className="mt-4 border-t border-border/50 pt-4">
                    <div className="mt-2 flex flex-col gap-3">
                      {shouldShowSign && <SignToggle />}
                      {visibleHeaderButtons.map((item, i) => {
                        return (
                          <Button
                            key={i}
                            variant={item.variant}
                            className="landing-hero-button bg-primary"
                          >
                            <Link
                              href={item.url as any}
                              target={item.target || ""}
                              className="flex items-center gap-1"
                              onClick={() => trackMarketplaceView(item.url)}
                            >
                              {item.title}
                              {item.icon && (
                                <Icon
                                  name={item.icon}
                                  className="size-4 shrink-0"
                                />
                              )}
                            </Link>
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
